import {Component, forwardRef, Input, OnChanges} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormArray, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator} from '@angular/forms';
import {MaterialModule} from '../../../../material.module';
import {AdditionalCompanyProperty, CompanyPropertyAction, HubspotAdditionalProperties, HubspotProperty} from '../../../../models/hubspot.model';

type PropertyRow = FormGroup<{propertyName: FormControl<string>; value: FormControl<string>}>;

@Component({
  selector: 'app-additional-company-properties',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule],
  templateUrl: './additional-company-properties.component.html',
  styleUrls: ['./additional-company-properties.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AdditionalCompanyPropertiesComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => AdditionalCompanyPropertiesComponent), multi: true},
  ],
})
export class AdditionalCompanyPropertiesComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() properties: HubspotProperty[] = [];
  @Input() propertiesReady = false;
  @Input() propertiesError: string | null = null;
  @Input() portalId: number | null = null;
  @Input() connectionId?: string;
  @Input() protectedPropertyNames: string[] = [];
  selectedAction: CompanyPropertyAction = 'create';
  disabled = false;
  readonly actions: CompanyPropertyAction[] = ['create', 'update'];
  readonly form = new FormGroup({create: new FormArray<PropertyRow>([]), update: new FormArray<PropertyRow>([])});
  private binding: Pick<HubspotAdditionalProperties, 'portalId' | 'hubspotConnectionId'> | null = null;
  private hasConfiguration = false;
  private edited = false;
  private invalidConfiguration = false;
  private onChange: (value: HubspotAdditionalProperties) => void = () => {};
  private onTouched: () => void = () => {};
  private validationChanged: () => void = () => {};

  constructor() {
    this.form.valueChanges.subscribe(() => this.emitValue());
  }

  ngOnChanges(): void { this.validationChanged(); }
  rows(action = this.selectedAction): FormArray<PropertyRow> { return this.form.controls[action]; }
  property(name: string): HubspotProperty | undefined { return this.properties.find(property => property.name === name); }
  isWritable(property: HubspotProperty): boolean {
    return !property.archived && !property.calculated && !property.hasUniqueValue && !property.modificationMetadata?.readOnlyValue &&
      !property.name.startsWith('shine_enrich_') && !['hs_object_id', 'hs_country_code'].includes(property.name) && !this.protectedPropertyNames.includes(property.name) &&
      (['string', 'number', 'bool'].includes(property.type) ||
        (property.type === 'enumeration' && property.fieldType !== 'checkbox' && !property.externalOptions));
  }
  suggestions(value: string): HubspotProperty[] {
    const query = value.toLowerCase();
    return this.properties.filter(property => this.isWritable(property) &&
      (property.name.toLowerCase().includes(query) || property.label.toLowerCase().includes(query)));
  }
  get connectionChanged(): boolean {
    return !!this.binding && (this.binding.portalId !== this.portalId || this.binding.hubspotConnectionId !== (this.connectionId || undefined));
  }
  useCurrentConnection(): void {
    this.binding = null;
    this.invalidConfiguration = false;
    this.emitValue();
  }
  add(): void {
    if (this.disabled || this.rows().length >= 50) return;
    this.rows().push(this.makeRow({propertyName: '', value: ''}));
  }
  remove(index: number): void { this.rows().removeAt(index); }
  private makeRow(row: AdditionalCompanyProperty): PropertyRow {
    return new FormGroup({propertyName: new FormControl(row.propertyName, {nonNullable: true}), value: new FormControl(row.value, {nonNullable: true})});
  }
  writeValue(value: HubspotAdditionalProperties | null | undefined): void {
    this.edited = false;
    this.hasConfiguration = value != null;
    this.invalidConfiguration = !!value && (!value.objects?.companies ||
      this.actions.some(action => !Array.isArray(value.objects.companies[action])));
    this.binding = value ? {portalId: value.portalId, ...(value.hubspotConnectionId ? {hubspotConnectionId: value.hubspotConnectionId} : {})} : null;
    for (const action of this.actions) {
      this.rows(action).clear({emitEvent: false});
      if (!this.invalidConfiguration) for (const row of value?.objects.companies[action] || []) {
        this.rows(action).push(this.makeRow(row), {emitEvent: false});
      }
    }
    this.validationChanged();
  }
  private emitValue(): void {
    this.edited = true;
    this.hasConfiguration = true;
    const binding = this.binding || {portalId: this.portalId || 0, ...(this.connectionId ? {hubspotConnectionId: this.connectionId} : {})};
    this.onChange({...binding, objects: {companies: this.form.getRawValue()}});
    this.onTouched();
    this.validationChanged();
  }
  registerOnChange(fn: (value: HubspotAdditionalProperties) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  registerOnValidatorChange(fn: () => void): void { this.validationChanged = fn; }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.form.disable({emitEvent: false}); else this.form.enable({emitEvent: false});
  }
  rowError(action: CompanyPropertyAction, index: number): string | null {
    const row = this.rows(action).at(index).getRawValue();
    if (!/^[A-Za-z][A-Za-z0-9_]{0,127}$/.test(row.propertyName)) return 'Enter a HubSpot internal property name.';
    if (this.rows(action).getRawValue().filter(other => other.propertyName === row.propertyName).length > 1) return 'This property is already configured for this action.';
    const property = this.property(row.propertyName);
    if (!property || !this.isWritable(property)) return 'Select a supported, writable Company property from this portal.';
    if (!row.value.trim() || row.value.length > 4000) return 'Enter a value of up to 4,000 characters.';
    if (property.type === 'number' && (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(row.value) || !Number.isFinite(Number(row.value)))) return 'Enter a finite number, including 0.';
    if (property.type === 'bool' && !['true', 'false'].includes(row.value)) return 'Select true or false.';
    if (property.type === 'enumeration' && !property.options?.some(option => !option.hidden && option.value === row.value)) return 'Select an internal option value from this property.';
    return null;
  }
  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.hasConfiguration || !this.edited) return null;
    const hasRows = this.actions.some(action => this.rows(action).length > 0);
    return this.invalidConfiguration || this.connectionChanged || !this.portalId ||
      (hasRows && (!this.propertiesReady || !!this.propertiesError)) ||
      this.actions.some(action => this.rows(action).length > 50 || this.rows(action).controls.some((_, index) => this.rowError(action, index)))
      ? {additionalCompanyProperties: true} : null;
  }
}
